export interface MarketingFeature {
  slug: string;
  label: string;
  title: string;
  summary: string;
  bullets: string[];
  benefits: Array<{ title: string; body: string }>;
  faqs: Array<{ question: string; answer: string; unverified?: boolean }>;
  related: string[];
  sampleTitle: string;
  sampleRows: string[][];
}

export const marketingPaths = {
  home: '/website/medical-education',
  hospitals: '/website/hospitals-and-trusts',
  universities: '/website/universities-and-societies',
  providers: '/website/course-and-cpd-providers',
  trust: '/website/trust-centre',
  contact: '/website/contact-us',
  feature: (slug: string) => `/website/features/${slug}`,
};

export const externalPaths = {
  browseEvents: '/',
  createEvent: '/admin/event/create',
  login: '/auth/login',
  videoGuides: '/website/video-guides',
  platformTerms: '/website/platform-terms',
  bookingTerms: '/website/attendee-booking-cancellation-refund-terms',
  payoutTerms: '/website/host-payments-payout-terms',
};

export const marketingFeatures: MarketingFeature[] = [
  {
    slug: 'ai-timetabling',
    label: 'AI timetabling',
    title: 'Timetables in minutes, not weeks',
    summary:
      "Describe what you need and Careerbook's AI builds teaching timetables, placements, clinic schedules and rotas — then checks every one for clashes.",
    bullets: [
      'Describe the cohort, dates, sessions and educators, or upload last year’s timetable.',
      'Generate a full draft with room, educator and learner clashes flagged.',
      'Review, edit and publish directly to every learner’s calendar.',
    ],
    benefits: [
      { title: '2–4 weeks → 1–2 days', body: 'Typical time to build a block or rotation timetable, before and after.' },
      { title: 'Clashes caught early', body: 'Room, educator and learner conflicts are flagged before publication.' },
      { title: 'Always up to date', body: 'Change a session once and every affected calendar updates.' },
    ],
    faqs: [
      {
        question: 'Can we edit what the AI produces?',
        answer: 'Yes. Timetables remain fully editable before and after publishing.',
      },
      {
        question: 'What can it build?',
        answer: 'Teaching timetables, placement plans, doctor rotations, clinic schedules and staff rotas.',
      },
      {
        question: 'Is it included in our licence?',
        answer: 'It is available on hospital licences. Our team will confirm what is included for your organisation.',
      },
    ],
    related: ['smart-attendance', 'reminders-and-alerts', 'learner-portfolio'],
    sampleTitle: 'FY1 Block 3 — timetable draft',
    sampleRows: [
      ['Mon 09:00', 'Acute medicine', 'Dr Shah', 'Ready'],
      ['Tue 13:00', 'Simulation', 'Skills lab', 'Ready'],
      ['Thu 12:30', 'Sepsis teaching', 'Room 2', 'Clash fixed'],
    ],
  },
  {
    slug: 'smart-attendance',
    label: 'Smart attendance',
    title: 'Know who attended, how and where',
    summary:
      'Learners sign in by QR code, e-signature or a one-time location check. Every record shows how attendance was captured, so audits stop being guesswork.',
    bullets: [
      'Use QR code, e-signature or one-time location confirmation.',
      'Record the method and time against every attendance entry.',
      'See who delivered, rescheduled or cancelled teaching.',
    ],
    benefits: [
      { title: '100% accurate capture', body: 'Every record carries its method, time and session.' },
      { title: 'No paper, no chasing', body: 'Attendance is digital from the start.' },
      { title: 'Track delivery too', body: 'See delivered, rescheduled and cancelled teaching organisation-wide.' },
    ],
    faqs: [
      {
        question: 'Does location tracking follow learners?',
        answer: 'No. It records one position at sign-in only and never tracks anyone between sessions.',
      },
      {
        question: 'What if someone forgets to sign in?',
        answer: 'Admins can update attendance and record a reason, such as approved leave.',
      },
      {
        question: 'Does it work on community placements?',
        answer: 'Yes. QR and e-signature check-in work for hospital and community placements.',
      },
    ],
    related: ['reminders-and-alerts', 'reporting-and-insights', 'feedback-and-certificates'],
    sampleTitle: 'Sepsis teaching — live register',
    sampleRows: [
      ['A. Khan', 'QR code', '12:26', 'Present'],
      ['M. Evans', 'Location', '12:29', 'Present'],
      ['J. Brown', 'Admin update', '—', 'Study leave'],
    ],
  },
  {
    slug: 'feedback-and-certificates',
    label: 'Feedback and certificates',
    title: 'Feedback collected. Certificates issued. Nobody chased.',
    summary:
      'A feedback form goes out after every session and a certificate issues itself once it is complete — ready for portfolios, appraisal and revalidation.',
    bullets: [
      'Create reusable forms for a session or full programme.',
      'Send automatic prompts and reminders after attendance.',
      'Issue and store certificates as soon as feedback is complete.',
    ],
    benefits: [
      { title: 'Zero chasing', body: 'Automatic reminders help raise response rates.' },
      { title: 'Instant certificates', body: 'Issued and stored per learner, per session.' },
      { title: 'Useful insight', body: 'Review ratings and comments by session and educator.' },
    ],
    faqs: [
      { question: 'Can we customise the forms?', answer: 'Yes. Use your own questions for each session or programme.' },
      {
        question: 'Where do learners find certificates?',
        answer: 'Certificates remain in their Careerbook profile and can be downloaded at any time.',
      },
      {
        question: 'Can we see who has not given feedback?',
        answer: 'Yes. Outstanding feedback is clearly shown as pending.',
      },
    ],
    related: ['smart-attendance', 'learner-portfolio', 'reporting-and-insights'],
    sampleTitle: 'Session follow-up',
    sampleRows: [
      ['38 attendees', 'Feedback requested', 'Automatic'],
      ['31 complete', 'Certificates issued', '31'],
      ['7 pending', 'Reminder scheduled', 'Tomorrow'],
    ],
  },
  {
    slug: 'reminders-and-alerts',
    label: 'Reminders and alerts',
    title: 'Nothing falls through the cracks',
    summary:
      'Learners are reminded before every session. Admins get instant alerts when someone reaches the amber or red absence threshold set for their group.',
    bullets: [
      'Schedule reminders automatically instead of using group chats.',
      'Set separate amber and red absence thresholds per group.',
      'Notify the responsible admins by email and in the app.',
    ],
    benefits: [
      { title: '50% fewer emails', body: 'Automatic reminders and alerts replace manual chasing.' },
      { title: 'No WhatsApp groups', body: 'Communication stays in Careerbook.' },
      { title: 'Early warning', body: 'Spot at-risk learners before a supervision issue develops.' },
    ],
    faqs: [
      { question: 'Who receives alerts?', answer: 'The admins responsible for that learner’s group.' },
      {
        question: 'Where can I see a learner’s status?',
        answer: 'On their profile alongside bookings, attendance and absence information.',
      },
      {
        question: 'Can thresholds differ by group?',
        answer: 'Yes. Amber and red thresholds are configured separately per group.',
      },
    ],
    related: ['smart-attendance', 'reporting-and-insights', 'workflows-and-inductions'],
    sampleTitle: 'Foundation attendance alerts',
    sampleRows: [
      ['FY1 East', '92%', 'On track'],
      ['FY1 West', '78%', 'Amber'],
      ['FY2', '66%', 'Red alert sent'],
    ],
  },
  {
    slug: 'reporting-and-insights',
    label: 'Reporting and insights',
    title: 'Compliance reports in one click',
    summary:
      'Real-time dashboards show departments, cohorts and individuals. Export evidence for GMC, NHS England and internal audits, while AI highlights slipping engagement.',
    bullets: [
      'Filter attendance, bookings, feedback and delivery by any group or date.',
      'Use AI to identify declining engagement and groups at risk.',
      'Download or send an audit-ready report in one click.',
    ],
    benefits: [
      { title: 'Audit-ready evidence', body: 'Support GMC, NHS England, internal audit and funding returns.' },
      { title: 'AI insights', body: 'Highlight declining engagement, at-risk groups and top educators.' },
      { title: 'Delivery tracked', body: 'See whether teaching was delivered, rescheduled or cancelled.' },
    ],
    faqs: [
      {
        question: 'What can we report on?',
        answer: 'Attendance, bookings, feedback, lateness and delivery by group and date range.',
      },
      {
        question: 'Can we schedule reports?',
        answer:
          'Scheduling availability still needs confirmation. Reports can currently be downloaded or sent in one click.',
        unverified: true,
      },
      {
        question: 'Who can see reports?',
        answer: 'Only admins with the appropriate role-based permissions.',
      },
    ],
    related: ['reminders-and-alerts', 'smart-attendance', 'ai-timetabling'],
    sampleTitle: 'Monthly teaching report',
    sampleRows: [
      ['Medicine', '91% attendance', 'Improving'],
      ['Surgery', '84% attendance', 'Stable'],
      ['Paediatrics', '72% attendance', 'Review'],
    ],
  },
  {
    slug: 'workflows-and-inductions',
    label: 'Workflows and inductions',
    title: 'Inductions that track themselves',
    summary:
      'Build a pre-induction checklist once and assign it to a cohort. See exactly who completed each welcome pack, DBS, occupational health or ID-card task.',
    bullets: [
      'Build sequential or parallel task groups once.',
      'Enrol new joiners automatically through their group.',
      'Open named chase lists and per-person progress.',
    ],
    benefits: [
      { title: 'Copy, never rebuild', body: 'Duplicate last year’s workflow and edit only what changed.' },
      { title: 'Named chase lists', body: 'Know exactly who needs a reminder.' },
      { title: 'Per-person progress', body: 'Review a learner’s task status before a meeting.' },
    ],
    faqs: [
      {
        question: 'Sequential or parallel?',
        answer: 'Sequential steps unlock in order; parallel tasks such as DBS and ID can be completed in any order.',
      },
      { question: 'What do learners do?', answer: 'They read each task, complete the requirement and mark it done.' },
      { question: 'Can we reuse workflows?', answer: 'Yes. Copy and edit them for the next cohort.' },
    ],
    related: ['reminders-and-alerts', 'learner-portfolio', 'reporting-and-insights'],
    sampleTitle: 'August induction',
    sampleRows: [
      ['Welcome pack', '64 of 64', 'Complete'],
      ['Occupational health', '58 of 64', '6 to chase'],
      ['ID card', '49 of 64', '15 to chase'],
    ],
  },
  {
    slug: 'learner-portfolio',
    label: 'Learner portfolio',
    title: 'A calendar and portfolio for every learner',
    summary:
      'Every doctor and student sees teaching, bookings and attendance in one place — a personal record ready for ARCP, appraisal and revalidation.',
    bullets: [
      'Show a personal calendar filtered by role, grade and cohort.',
      'Keep attendance and certificates together as evidence.',
      'Make the record available on mobile, tablet and desktop.',
    ],
    benefits: [
      { title: 'Never miss teaching', body: 'Personal calendars and automatic reminders stay in sync.' },
      { title: 'Evidence on hand', body: 'Keep a complete record for ARCP, appraisal and revalidation.' },
      { title: 'Beyond the Trust', body: 'Discover external courses and conferences in the marketplace.' },
    ],
    faqs: [
      {
        question: 'Can learners download certificates?',
        answer: 'Yes. They remain available from the learner profile.',
      },
      { question: 'Do learners need an app?', answer: 'No. Careerbook works in any modern browser.' },
      {
        question: 'What happens if they move Trust?',
        answer: 'Portability of a learner record between organisations still needs confirmation.',
        unverified: true,
      },
    ],
    related: ['feedback-and-certificates', 'smart-attendance', 'ai-timetabling'],
    sampleTitle: 'My education record',
    sampleRows: [
      ['Upcoming', 'Simulation training', '14 Oct'],
      ['Certificate', 'Acute medicine', 'Downloaded'],
      ['Attendance', 'This rotation', '91%'],
    ],
  },
  {
    slug: 'careerbook-live',
    label: 'Careerbook Live',
    title: 'Host, sell and follow up — all in one place',
    summary:
      'Run live online teaching without a separate video platform. Bookings, payments, attendance, feedback and certificates stay connected to the same event.',
    bullets: [
      'Publish a live or hybrid event to the Careerbook audience.',
      'Manage ticketing, payments and attendance in one portal.',
      'Send feedback and certificates automatically after the session.',
    ],
    benefits: [
      { title: 'One tool, not four', body: 'Replace separate video, booking, feedback and certificate tools.' },
      { title: 'Reach further', body: 'Stream to Careerbook’s audience of clinicians and learners.' },
      { title: 'Paid events built in', body: 'Sell tickets and manage refunds from one payment portal.' },
    ],
    faqs: [
      {
        question: 'Can sessions be recorded?',
        answer: 'Recording and on-demand availability still needs confirmation.',
        unverified: true,
      },
      {
        question: 'How many people can join?',
        answer: 'Attendee limits still need confirmation.',
        unverified: true,
      },
      {
        question: 'Is attendance captured online?',
        answer: 'The exact online attendance method still needs confirmation.',
        unverified: true,
      },
    ],
    related: ['feedback-and-certificates', 'learner-portfolio', 'reporting-and-insights'],
    sampleTitle: 'Live webinar',
    sampleRows: [
      ['Bookings', '412', 'Open'],
      ['Ticket revenue', 'Connected', 'Live'],
      ['Follow-up', 'Feedback + certificate', 'Automatic'],
    ],
  },
];

export const getMarketingFeature = (slug: string): MarketingFeature | undefined =>
  marketingFeatures.find((feature) => feature.slug === slug);
